// Table chat and quick reactions, the web twin of ChatPanelWidget on Android. It renders what the
// server sends and never invents a message of its own: your own text appears only when it comes
// back from the server, so everyone sees the same wording, sanitised the same way, in the same
// order.

import { el, clear } from './dom.js';
import { ServerProtocol } from '../generated/protocol.js';

// The emoji arrives with the server catalogue (it is language neutral and shared by every client);
// only the spoken labels live here, exactly as they live in strings.xml on Android
const EMOTE_LABELS = {
  GREETING: 'Ciao!',
  NICE_MOVE: 'Bella!',
  LUCKY: 'Che culo!',
  THINKING: 'Mmm...',
  HURRY_UP: 'Sbrigati!',
  OH_NO: 'Ahia!',
  OOPS: 'Scusa!',
  WELL_PLAYED: 'Ben giocato',
  LAUGH: 'Ahahah!',
  THANKS: 'Grazie!',
};

const EmoteFlashMillis = 2500;
const MaxRenderedMessages = 50;

export class ChatPanel {
  #root;
  #messages;
  #scroll;
  #empty;
  #panel;
  #bubble;
  #badge;
  #flash;
  #input;
  #emoteRow;
  #handlers;

  #open = false;
  #unread = 0;
  // Server sequence of the last rendered message: the backlog sent on join or reconnection can
  // overlap what already arrived live, and rendering those again would duplicate them
  #lastSequenceShown = 0;
  #flashTimer = null;
  #emojiByName = new Map();

  // handlers: { onSendText, onSendEmote }
  constructor(handlers) {
    this.#handlers = handlers;
    this.#build();
  }

  get element() {
    return this.#root;
  }

  #build() {
    this.#flash = el('div', { className: 'chat-flash', hidden: true });

    this.#badge = el('span', { className: 'chat-badge', hidden: true });
    this.#bubble = el(
      'button',
      {
        className: 'chat-bubble',
        type: 'button',
        'aria-label': 'Apri la chat',
        onclick: () => this.#setOpen(true),
      },
      [el('span', { text: '💬' }), this.#badge],
    );

    this.#messages = el('div', { className: 'chat-messages' });
    this.#scroll = el('div', { className: 'chat-scroll' }, [this.#messages]);
    this.#empty = el('p', { className: 'chat-empty', text: 'Nessun messaggio: rompi il ghiaccio.' });
    this.#emoteRow = el('div', { className: 'chat-emotes' });

    this.#input = el('input', {
      className: 'chat-input',
      type: 'text',
      maxlength: '200',
      placeholder: 'Scrivi al tavolo...',
      onkeydown: (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.#sendTyped();
        }
      },
    });

    this.#panel = el('section', { className: 'chat-panel', hidden: true }, [
      el('header', { className: 'chat-header' }, [
        el('span', { text: 'Chat del tavolo' }),
        el('button', {
          className: 'chat-close',
          type: 'button',
          text: '✕',
          'aria-label': 'Chiudi la chat',
          onclick: () => this.#setOpen(false),
        }),
      ]),
      this.#scroll,
      this.#empty,
      this.#emoteRow,
      el('div', { className: 'chat-compose' }, [
        this.#input,
        el('button', {
          className: 'button button-gold',
          type: 'button',
          text: 'Invia',
          onclick: () => this.#sendTyped(),
        }),
      ]),
    ]);

    this.#root = el('div', { className: 'chat', hidden: true }, [
      this.#flash,
      this.#bubble,
      this.#panel,
    ]);
  }

  // One button per catalogue entry, so an emote added on the server needs no change here beyond
  // its label
  setEmotes(emotes) {
    clear(this.#emoteRow);
    this.#emojiByName.clear();
    for (const entry of emotes) {
      const name = entry[ServerProtocol.ChatEmoteID];
      const emoji = entry[ServerProtocol.EmojiID] ?? '';
      this.#emojiByName.set(name, emoji);
      this.#emoteRow.append(
        el('button', {
          className: 'chip',
          type: 'button',
          text: `${emoji} ${EMOTE_LABELS[name] ?? name}`,
          onclick: () => this.#handlers.onSendEmote?.(name),
        }),
      );
    }
  }

  show() {
    this.#root.hidden = false;
  }

  hide() {
    this.#root.hidden = true;
  }

  #sendTyped() {
    const text = this.#input.value.trim();
    if (!text) return;
    this.#handlers.onSendText?.(text);
    this.#input.value = '';
  }

  #setOpen(open) {
    this.#open = open;
    this.#panel.hidden = !open;
    this.#bubble.hidden = open;
    if (open) {
      this.#unread = 0;
      this.#badge.hidden = true;
      this.#scrollToBottom();
      this.#input.focus();
    }
  }

  addHistory(messages) {
    for (const message of messages) {
      this.#add(message, true);
    }
  }

  addMessage(message) {
    this.#add(message, false);
  }

  #add(message, fromHistory) {
    const sequence = message[ServerProtocol.ChatSequenceID] ?? 0;
    if (sequence !== 0 && sequence <= this.#lastSequenceShown) {
      return;
    }
    this.#lastSequenceShown = Math.max(this.#lastSequenceShown, sequence);

    const sender = message[ServerProtocol.ChatSenderNameID] ?? '?';
    const emoteName = message[ServerProtocol.ChatEmoteID];
    let line;
    if (emoteName) {
      const emoji = this.#emojiByName.get(emoteName) ?? '';
      line = `${sender} ${emoji} ${EMOTE_LABELS[emoteName] ?? emoteName}`;
    } else {
      const text = message[ServerProtocol.ChatTextID] ?? '';
      if (!text) return;
      line = `${sender}: ${text}`;
    }

    this.#appendLine(line);

    // The backlog is catching up, not news: it must neither raise the badge nor flash on the
    // table, or reconnecting would replay every reaction at once
    if (fromHistory) return;
    if (emoteName) this.#flashEmote(line);
    if (!this.#open) {
      this.#unread += 1;
      this.#badge.textContent = String(this.#unread);
      this.#badge.hidden = false;
    }
  }

  #appendLine(line) {
    this.#empty.hidden = true;
    this.#messages.append(el('p', { className: 'chat-line', text: line }));
    while (this.#messages.childElementCount > MaxRenderedMessages) {
      this.#messages.firstElementChild.remove();
    }
    this.#scrollToBottom();
  }

  #scrollToBottom() {
    this.#scroll.scrollTop = this.#scroll.scrollHeight;
  }

  #flashEmote(line) {
    this.#flash.textContent = line;
    this.#flash.hidden = false;
    // A new reaction resets the timer instead of queueing: with four players tapping, the last one
    // is the one worth reading
    clearTimeout(this.#flashTimer);
    this.#flashTimer = setTimeout(() => {
      this.#flash.hidden = true;
    }, EmoteFlashMillis);
  }
}
